// The lesson export (EDU-7/EDU-8, ARCH §29.9, D-TF-153). PURE (ENG-1): the server's HTTP
// route and the solo download both serialize through here, so the two can never disagree
// about what leaves the game. Nothing about a student goes out beyond §29.6's result row,
// their display name, subject and role, and the lesson log — the schema test asserts it.
import { EDU } from '../config';
import type { LessonLogEntry, LessonResult, WorldLesson } from './results';

/** One student as the export sees them: keyed by identity subject (EDU-8). */
export interface ExportPlayer {
  sub: string;
  name: string;
  /** `guest` for a guest subject, else their world role. */
  role: string;
  lessons: Record<string, LessonResult>;
}

export interface ExportInput {
  world: { id: string; name: string };
  lesson: WorldLesson | null;
  players: readonly ExportPlayer[];
  log: readonly LessonLogEntry[];
}

/** `results.json` (§29.9). `truncated` is present only when rows were dropped at the cap. */
export interface ResultsDoc {
  world: { id: string; name: string };
  lesson: WorldLesson | null;
  players: ExportPlayer[];
  log: LessonLogEntry[];
  truncated?: number;
}

/** The export's rows, capped at `EDU.EXPORT_MAX_ROWS` (§29.9) — ordered by name then
 *  subject so two exports of the same world are identical. */
function capped<T extends { name: string; sub: string }>(rows: readonly T[]): { rows: T[]; dropped: number } {
  const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name) || a.sub.localeCompare(b.sub));
  return { rows: sorted.slice(0, EDU.EXPORT_MAX_ROWS), dropped: Math.max(0, sorted.length - EDU.EXPORT_MAX_ROWS) };
}

/** The world's results as a JSON document (§29.9): world, lesson meta, every player's
 *  `lessons`, the log. Only those fields — each player carries exactly sub/name/role/lessons. */
export function resultsJson(input: ExportInput): ResultsDoc {
  const { rows, dropped } = capped(input.players);
  const doc: ResultsDoc = {
    world: { id: input.world.id, name: input.world.name },
    lesson: input.lesson ? { ...input.lesson } : null,
    players: rows.map((p) => ({ sub: p.sub, name: p.name, role: p.role, lessons: p.lessons })),
    log: input.log.map((e) => ({ tick: e.tick, instructor: e.instructor, action: e.action, target: e.target })),
  };
  if (dropped > 0) doc.truncated = dropped;
  return doc;
}

/** RFC 4180 field quoting. */
function cell(v: string | number): string {
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** `<lesson>.csv` (§29.9): one header, then one row per student who has a row for
 *  `lesson` — `name, subject, role, objective:<name>…, quizBest, quizAttempts, ticksOnTask,
 *  completedTick`. An unfinished objective / quiz / completion is an empty cell. Past the
 *  cap, one final `# truncated: <n> more rows` line states what was dropped. */
export function lessonCsv(input: ExportInput, lesson: string, objectiveNames: readonly string[]): string {
  const withRow = input.players.filter((p) => p.lessons[lesson] !== undefined);
  const { rows, dropped } = capped(withRow);
  const header = ['name', 'subject', 'role', ...objectiveNames.map((o) => `objective:${o}`), 'quizBest', 'quizAttempts', 'ticksOnTask', 'completedTick'];
  const lines = [header.map(cell).join(',')];
  for (const p of rows) {
    const r = p.lessons[lesson]!;
    lines.push([
      p.name, p.sub, p.role,
      ...objectiveNames.map((o) => r.objectives[o] ?? ''),
      r.quiz?.best ?? '', r.quiz?.attempts ?? '', r.ticksOnTask, r.completed ?? '',
    ].map(cell).join(','));
  }
  if (dropped > 0) lines.push(`# truncated: ${dropped} more rows`);
  return lines.join('\n') + '\n';
}
